import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import ExplorerLink from 'src/common/components/Link/ExplorerLink'
import { Offer } from 'src/common/types/models/Offer'
import OfferActions from '../OfferActions.tsx/OfferActions'
import './styles.css'
import { FULL_DATE_TIME_FORMAT } from 'src/common/consts/dateFormats'
import useAuthorizations from 'src/common/queries/useAuthorizations/useAuthorizations'
import { useMemo } from 'react'

interface OfferDetailsProps {
  offer: Offer
  onCancel?: (offer: Offer) => void
}

const emptyValue = '--'

const OfferDetails: React.FC<OfferDetailsProps> = ({ offer, onCancel }) => {
  const {
    filterAuthorizationsByAddresses,
    checkOneOfAddressesIsAuthorized,
    isLoading: authLoading,
  } = useAuthorizations(offer.authorizationAddresses)
  const { t } = useTranslation('offersManagement', {
    keyPrefix: 'offerDetails',
  })

  const fullAuthorizations = useMemo(() => {
    if (!offer.authorizationAddresses || authLoading) return []

    return filterAuthorizationsByAddresses(offer.authorizationAddresses)
  }, [
    authLoading,
    filterAuthorizationsByAddresses,
    offer.authorizationAddresses,
  ])

  const { depositAsset, withdrawalAsset } = offer

  const filledAmount = offer.amountIn.minus(offer.availableAmount)
  const filled = `${filledAmount}/${offer.amountIn} ${depositAsset.symbol}`

  const formattedDate = dayjs
    .unix(offer.expiryTimestamp)
    .format(FULL_DATE_TIME_FORMAT)

  const isAuthorized = Boolean(
    offer.authorizationAddresses &&
      checkOneOfAddressesIsAuthorized(offer.authorizationAddresses)
  )

  return (
    <div className='comp-offer-details'>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('id')}</span>
        <div className='comp-offer-details-value'>{offer.id}</div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('maker')}</span>
        <div className='comp-offer-details-value'>
          <ExplorerLink
            className='group hover:text-primary'
            iconClassName='group-hover:fill-primary fill-main-accent'
            hash={offer.maker}
            type={'address'}
          />
        </div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('filled')}</span>
        <div className='comp-offer-details-value'>{filled}</div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('privateAddresses')}</span>
        <div className='comp-offer-details-value'>
          {offer.specialAddresses
            ? offer.specialAddresses.map(address => (
                <ExplorerLink
                  key={address}
                  className='group hover:text-primary'
                  iconClassName='group-hover:fill-primary fill-main-accent'
                  hash={address}
                  type={'address'}
                />
              ))
            : emptyValue}
        </div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('expiry')}</span>
        <div className='comp-offer-details-value'>{formattedDate}</div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('assetSell')}</span>
        <div className='comp-offer-details-value'>
          <ExplorerLink
            className='group hover:text-primary'
            iconClassName='group-hover:fill-primary fill-main-accent'
            hash={depositAsset.contractAddress}
            type={'address'}
          />
        </div>
      </div>
      <div className='comp-offer-details-item typo-label2'>
        <span>{t('assetBuy')}</span>
        <div className='comp-offer-details-value'>
          <ExplorerLink
            className='group hover:text-primary'
            iconClassName='group-hover:fill-primary fill-main-accent'
            hash={withdrawalAsset.contractAddress}
            type={'address'}
          />
        </div>
      </div>
      {depositAsset.priceFeedAddress && withdrawalAsset.priceFeedAddress && (
        <>
          <div className='comp-offer-details-item typo-label2'>
            <span>{t('assetSellPriceFeed')}</span>
            <div className='comp-offer-details-value'>
              <ExplorerLink
                className='group hover:text-primary'
                iconClassName='group-hover:fill-primary fill-main-accent'
                hash={depositAsset.priceFeedAddress}
                type={'address'}
              />
            </div>
          </div>
          <div className='comp-offer-details-item typo-label2'>
            <span>{t('assetBuyPriceFeed')}</span>
            <div className='comp-offer-details-value'>
              <ExplorerLink
                className='group hover:text-primary'
                iconClassName='group-hover:fill-primary fill-main-accent'
                hash={withdrawalAsset.priceFeedAddress}
                type={'address'}
              />
            </div>
          </div>
        </>
      )}
      <OfferActions
        isAuthorized={isAuthorized}
        authLoading={authLoading}
        offer={offer}
        className='mt-2'
        onCancel={onCancel}
      />
    </div>
  )
}

export default OfferDetails
